import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { POPULAR_BRANDS } from "../../constants/brands";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_FORM = {
  brand: "",
  quantity: "",
  expiration_date: "",
  condition: "excellent",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  notes: ""
};

const CONDITION_OPTIONS = [
  { value: "excellent", label: "Excellent", desc: "Like new, no damage" },
  { value: "good", label: "Good", desc: "Minor wear, seals intact" },
  { value: "fair", label: "Fair", desc: "Some wear, seals still intact" }
];

export const SubmissionFormDialog = ({ open, onOpenChange }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/submissions`, {
        ...formData,
        quantity: parseInt(formData.quantity)
      });
      toast.success("Submission received!", {
        description: "We'll contact you within 24-48 hours with an offer."
      });
      onOpenChange(false);
      setFormData(INITIAL_FORM);
    } catch (error) {
      toast.error("Submission failed", {
        description: error.response?.data?.detail || "Please try again later."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#1F2937]" style={{ fontFamily: "Manrope" }}>
            Sell Your Test Strips
          </DialogTitle>
          <DialogDescription>
            Fill out the form below and we'll get back to you with an offer within 24-48 hours.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4" data-testid="submission-form">
          <div className="space-y-2">
            <Label className="label-style">Brand *</Label>
            <Select value={formData.brand} onValueChange={(value) => handleSelectChange("brand", value)} required>
              <SelectTrigger className="h-12" data-testid="brand-select">
                <SelectValue placeholder="Select brand" />
              </SelectTrigger>
              <SelectContent>
                {POPULAR_BRANDS.map((brand) => (
                  <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="label-style">Number of Boxes *</Label>
            <Input
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleInputChange}
              placeholder="e.g., 5"
              min="1"
              className="h-12"
              required
              data-testid="quantity-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="label-style">Expiration Date *</Label>
            <Input
              type="date"
              name="expiration_date"
              value={formData.expiration_date}
              onChange={handleInputChange}
              className="h-12"
              required
              data-testid="expiration-input"
            />
            <p className="text-xs text-[#4B5563]">Must be at least 6 months from today</p>
          </div>

          <div className="space-y-3">
            <Label className="label-style">Box Condition *</Label>
            <RadioGroup
              value={formData.condition}
              onValueChange={(value) => handleSelectChange("condition", value)}
              className="flex flex-col gap-3"
              data-testid="condition-radio"
            >
              {CONDITION_OPTIONS.map((option) => (
                <div key={option.value} className="flex items-center space-x-3">
                  <RadioGroupItem value={option.value} id={option.value} />
                  <Label htmlFor={option.value} className="font-normal cursor-pointer">
                    <span className="font-medium">{option.label}</span>
                    <span className="text-[#4B5563] text-sm ml-2">- {option.desc}</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="pt-4 border-t border-gray-100">
            <h4 className="font-semibold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>
              Contact Information
            </h4>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="label-style">Your Name *</Label>
                <Input
                  type="text"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleInputChange}
                  placeholder="John Doe"
                  className="h-12"
                  required
                  data-testid="name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="label-style">Email Address *</Label>
                <Input
                  type="email"
                  name="contact_email"
                  value={formData.contact_email}
                  onChange={handleInputChange}
                  placeholder="john@example.com"
                  className="h-12"
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="label-style">Phone Number (Optional)</Label>
                <Input
                  type="tel"
                  name="contact_phone"
                  value={formData.contact_phone}
                  onChange={handleInputChange}
                  placeholder="(555) 123-4567"
                  className="h-12"
                  data-testid="phone-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="label-style">Additional Notes (Optional)</Label>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Any additional details about your test strips..."
                  rows={3}
                  data-testid="notes-input"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-[#4B5563] bg-[#F5F5F0] p-3 rounded-lg">
            <Shield className="w-5 h-5 text-[#0F5132]" />
            <span>Your information is secure and will never be shared.</span>
          </div>

          <Button
            type="submit"
            className="w-full bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-full py-6 text-lg font-semibold"
            disabled={isSubmitting}
            data-testid="submit-btn"
          >
            {isSubmitting ? "Submitting..." : "Submit for Offer"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
